import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Building2, UserPlus, X } from 'lucide-react';
import { corpSubsAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminCorporateSeats() {
  const [account, setAccount] = useState(null);
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [createForm, setCreateForm] = useState({ name: '', seats: 5, tier: 'corporate' });

  const refresh = () => {
    setLoading(true);
    corpSubsAPI
      .mine()
      .then((r) => {
        const acct = r.data?.account;
        if (!acct) {
          setAccount(null);
          setSeats([]);
          return;
        }
        return corpSubsAPI.get(acct.id).then((rr) => {
          setAccount(rr.data.account);
          setSeats(rr.data.seats || []);
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const create = async () => {
    if (!createForm.name) return toast.error('Account name required');
    try {
      await corpSubsAPI.create(createForm);
      toast.success('Corporate account created');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create account');
    }
  };

  const addSeat = async () => {
    if (!newEmail.includes('@')) return toast.error('Valid email required');
    try {
      await corpSubsAPI.addSeat(account.id, newEmail);
      toast.success(`Seat added for ${newEmail}`);
      setNewEmail('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add seat');
    }
  };

  const removeSeat = async (email) => {
    try {
      await corpSubsAPI.removeSeat(account.id, email);
      toast.success(`Removed ${email}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove seat');
    }
  };

  if (loading) return <Skeleton className="h-72 rounded-xl" />;

  if (!account) {
    return (
      <div className="space-y-4 max-w-lg" data-testid="admin-corp-seats-empty">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Building2 className="w-6 h-6 text-[#B89B5E]" /> Corporate Seats</h1>
        <p className="text-sm text-[#5B616E]">No corporate account on file. Create one to manage seats and shared billing.</p>
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-5 space-y-3">
            <Input placeholder="Account name (e.g. Acme Corp)" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
            <Input type="number" min={1} placeholder="Seats" value={createForm.seats} onChange={(e) => setCreateForm({ ...createForm, seats: parseInt(e.target.value, 10) })} />
            <Button className="bg-[#121316] text-white hover:bg-[#1B1D22] w-full" onClick={create}>Create corporate account</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const usage = `${seats.length} of ${account.seats_purchased}`;
  const seatsLeft = (account.seats_purchased || 0) - seats.length;

  return (
    <div className="space-y-4" data-testid="admin-corp-seats-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Building2 className="w-6 h-6 text-[#B89B5E]" /> {account.name}</h1>
        <p className="text-sm text-[#5B616E] mt-1">{account.tier?.toUpperCase()} account · seats {usage} ({seatsLeft} available)</p>
      </div>
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Add seat</CardTitle></CardHeader>
        <CardContent className="flex gap-2 items-center">
          <Input placeholder="user@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="flex-1" />
          <Button onClick={addSeat} disabled={seatsLeft <= 0} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
            <UserPlus className="w-4 h-4 mr-2" /> Assign
          </Button>
        </CardContent>
      </Card>
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Active seats</CardTitle></CardHeader>
        <CardContent>
          <div className="divide-y divide-[#E7E0D6]">
            {seats.map((s) => (
              <div key={s.email} className="flex items-center gap-3 py-2 text-sm">
                <span className="flex-1">{s.email}</span>
                {s.is_owner ? (
                  <Badge variant="outline" className="text-[10px]">Owner</Badge>
                ) : (
                  <button onClick={() => removeSeat(s.email)} className="text-[#B3261E] hover:bg-red-50 rounded p-1" title="Remove seat" data-testid="seat-remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
